
import React from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Star } from 'lucide-react';

interface QuickStartGuideProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const QuickStartGuide: React.FC<QuickStartGuideProps> = ({
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const steps = [
    {
      title: 'Create your first article',
      description: 'Click "New Article" in your dashboard to start writing. Begin with something that showcases your expertise.',
      tips: [
        'Start with a compelling headline',
        'Include a quality featured image',
        'Write a strong introduction that hooks readers',
        'Aim for 800-1500 words for in-depth coverage'
      ]
    },
    {
      title: 'Share with your audience',
      description: 'Once published, share your article on social media to attract your existing audience to DirectPress.',
      tips: [
        'Use the built-in social sharing tools',
        'Share a compelling excerpt to generate interest',
        'Tag relevant topics and communities',
        'Engage with comments on your posts'
      ]
    },
    {
      title: 'Interact with readers',
      description: 'Build community by responding to comments and engaging with your audience directly.',
      tips: [
        'Set aside time to respond to reader comments',
        'Ask questions to encourage discussion',
        'Thank readers for valuable feedback',
        'Address constructive criticism professionally'
      ]
    },
    {
      title: 'Analyze performance',
      description: 'Check your dashboard analytics to see what content resonates with your audience.',
      tips: [
        'Review which topics generate the most engagement',
        'Note reading time and completion rates',
        'Track subscriber growth after each article',
        'Use insights to plan future content'
      ]
    }
  ];

  return (
    <OnboardingLayout
      title="Quick Start Guide"
      subtitle="Follow these steps to launch your presence on DirectPress"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-3xl mx-auto">
        <div className="space-y-6">
          {steps.map((step, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">{step.title}</h3>
                    <p className="text-muted-foreground mb-4">{step.description}</p>
                    
                    <div className="bg-muted/30 rounded-md p-3">
                      <h4 className="flex items-center text-sm font-medium mb-2">
                        <Star className="h-4 w-4 mr-1 text-primary" />
                        Pro Tips
                      </h4>
                      <ul className="space-y-1">
                        {step.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start text-sm">
                            <Check className="h-4 w-4 mr-2 text-primary mt-0.5" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4 mt-6">
          <h4 className="text-sm font-medium mb-2">Publishing cadence:</h4>
          <p className="text-sm text-muted-foreground">
            For optimal audience growth, we recommend publishing at least once per week.
            Consistency is more important than frequency - establish a schedule you can maintain.
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default QuickStartGuide;
